// B130 Wave 2C Phase 11 — shared second-hand-rule helpers extracted
// from buildPortfolioContext.ts (Phase 10 inline) so TaxPage.tsx can
// drive its banners off the same logic without re-importing the AI
// prompt builder. Both helpers are pure / synchronous / type-only —
// no React, no DOM, no side effects.

import type { PropertyRecord } from "../types/portfolio";

/**
 * Applicability of the ITAA s40-27 second-hand depreciating-asset rule
 * for a property. Pre-9-May-2017 contracts are grandfathered; non-
 * residential properties are always exempt; missing contract date is
 * treated conservatively as "yes" (rule applies). Mirrors the
 * eligibility engine's short-circuit order without re-running the full
 * per-item check.
 */
export function secondHandRuleApplies(
  p: PropertyRecord,
): "yes" | "no" | "non-residential" {
  if (p.assetClass !== "Residential" && p.assetClass !== "Mixed Use") {
    return "non-residential";
  }
  const contractDate = p.contractDate ?? p.purchaseDate;
  if (!contractDate) return "yes";
  return contractDate >= "2017-05-09" ? "yes" : "no";
}

/**
 * True when the structure is one of the four ITAA s40-27(2) excluded
 * entities — companies (Pty Ltd), public-unit-trusts, MITs, APRA-
 * regulated super funds. SMSFs are NOT excluded entities; individuals,
 * discretionary trusts, family trusts, and partnerships of individuals
 * are also NOT excluded.
 *
 * `null` / `undefined` structure → personal-name property → not an
 * excluded entity.
 *
 * Subset type — accepts both the full `Structure` interface and the
 * `Div40EligibilityOwnerStructure` calc-layer subset, since the only
 * field this helper reads is `excludedEntityType`.
 */
export function entityHasExcludedEntityException(
  s: { excludedEntityType?: string | null } | null | undefined,
): boolean {
  if (!s) return false;
  const v = s.excludedEntityType;
  return (
    v === "company" ||
    v === "public-unit-trust" ||
    v === "MIT" ||
    v === "APRA-super"
  );
}
